<template>
  <div v-if="visible" class="leo-operator-assistant-host">
    <button
      v-show="showFab"
      type="button"
      class="leo-operator-assistant-fab"
      :class="{ 'is-dragging': fabDragging, 'is-open': assistantOpen }"
      :style="fabStyle"
      title="Leo SOC助手（可拖动）"
      data-testid="leo-soc-assistant-fab"
      @mousedown.prevent="onFabDown"
      @click="onFabClick"
    >
      Leo
    </button>
    <leo-training-operator-assistant />
  </div>
</template>

<script>
import LeoTrainingOperatorAssistant from '@/components/LeoKnowledge/LeoTrainingOperatorAssistant.vue'
import {
  isLeoOperatorAssistantOpen,
  leoOperatorAssistantBus,
  LEO_SOC_ASSISTANT_UNIFIED_CHAT,
  openLeoOperatorAssistant,
  openLeoOperatorAssistantChat,
  closeLeoOperatorAssistant,
  routeShowsOperatorAssistant
} from '@/utils/leoOperatorAssistant'
import { fetchActiveSmartGuideSession } from '@/api/socSmartGuide'
import { getAssistantFabPos, setAssistantFabPos } from '@/utils/leoOperatorAssistantLayout'

const VIEW_PAD = 8
const FAB_W = 56
const FAB_H = 56
const CLICK_SLOP = 6

export default {
  name: 'LeoTrainingOperatorAssistantHost',
  components: { LeoTrainingOperatorAssistant },
  data () {
    return {
      fabPos: null,
      fabDragging: false,
      fabMoved: false,
      dragDx: 0,
      dragDy: 0,
      assistantOpen: false
    }
  },
  computed: {
    visible () {
      return routeShowsOperatorAssistant(this.$route)
    },
    /** 主布局常驻显示 Leo 浮球；助手开着时仍显示，点击可关闭／再开 */
    showFab () {
      return true
    },
    fabStyle () {
      if (this.fabPos && typeof this.fabPos.left === 'number' && typeof this.fabPos.top === 'number') {
        return {
          left: this.fabPos.left + 'px',
          top: this.fabPos.top + 'px',
          right: 'auto',
          bottom: 'auto'
        }
      }
      return {
        right: '24px',
        bottom: '88px',
        left: 'auto',
        top: 'auto'
      }
    }
  },
  mounted () {
    this.restoreFabPos()
    this.assistantOpen = isLeoOperatorAssistantOpen()
    leoOperatorAssistantBus.$on('assistant-panel-open', this.onAssistantOpen)
    window.addEventListener('resize', this.onViewportResize)
  },
  beforeDestroy () {
    window.removeEventListener('mousemove', this.onFabMove)
    window.removeEventListener('mouseup', this.onFabUp)
    window.removeEventListener('resize', this.onViewportResize)
    leoOperatorAssistantBus.$off('assistant-panel-open', this.onAssistantOpen)
  },
  methods: {
    restoreFabPos () {
      const vw = typeof window !== 'undefined' ? window.innerWidth : 0
      const vh = typeof window !== 'undefined' ? window.innerHeight : 0
      const pos = getAssistantFabPos({ width: vw, height: vh, pad: VIEW_PAD, fabW: FAB_W, fabH: FAB_H })
      if (!pos) {
        this.fabPos = null
        return
      }
      this.fabPos = {
        left: Math.min(Math.max(VIEW_PAD, pos.left), Math.max(VIEW_PAD, vw - FAB_W - VIEW_PAD)),
        top: Math.min(Math.max(VIEW_PAD, pos.top), Math.max(VIEW_PAD, vh - FAB_H - VIEW_PAD))
      }
    },
    onViewportResize () {
      if (!this.fabPos) return
      this.restoreFabPos()
    },
    onAssistantOpen (open) {
      this.assistantOpen = !!open
    },
    onFabClick (e) {
      if (this.fabMoved) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      this.openFromFab()
    },
    openFromFab () {
      if (isLeoOperatorAssistantOpen()) {
        closeLeoOperatorAssistant()
        return
      }
      const openDefault = () => {
        if (LEO_SOC_ASSISTANT_UNIFIED_CHAT) {
          openLeoOperatorAssistantChat()
        } else {
          openLeoOperatorAssistant()
        }
      }
      fetchActiveSmartGuideSession().then(function (res) {
        const data = res && res.data
        if (data && data.active && data.status !== 'COMPLETED') {
          openLeoOperatorAssistant({ mode: 'guide' })
          return
        }
        openDefault()
      }).catch(function () {
        openDefault()
      })
    },
    onFabDown (e) {
      if (e.button !== 0) return
      const el = e.currentTarget
      const rect = el.getBoundingClientRect()
      this.fabDragging = true
      this.fabMoved = false
      this.dragDx = e.clientX - rect.left
      this.dragDy = e.clientY - rect.top
      if (!this.fabPos) {
        this.fabPos = { left: rect.left, top: rect.top }
      }
      window.addEventListener('mousemove', this.onFabMove)
      window.addEventListener('mouseup', this.onFabUp)
    },
    onFabMove (e) {
      if (!this.fabDragging) return
      let left = e.clientX - this.dragDx
      let top = e.clientY - this.dragDy
      const vw = window.innerWidth
      const vh = window.innerHeight
      left = Math.min(Math.max(VIEW_PAD, left), vw - FAB_W - VIEW_PAD)
      top = Math.min(Math.max(VIEW_PAD, top), vh - FAB_H - VIEW_PAD)
      const prev = this.fabPos || { left, top }
      if (Math.abs(left - prev.left) > CLICK_SLOP || Math.abs(top - prev.top) > CLICK_SLOP) {
        this.fabMoved = true
      }
      this.fabPos = { left, top }
    },
    onFabUp () {
      if (!this.fabDragging) return
      this.fabDragging = false
      window.removeEventListener('mousemove', this.onFabMove)
      window.removeEventListener('mouseup', this.onFabUp)
      if (this.fabPos) {
        setAssistantFabPos(this.fabPos.left, this.fabPos.top)
      }
      // 下一 tick 再清 fabMoved，避免同次 mouseup+click 误开
      setTimeout(() => {
        this.fabMoved = false
      }, 0)
    }
  }
}
</script>

<style scoped>
.leo-operator-assistant-fab {
  position: fixed;
  z-index: 100060;
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  padding: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #fff;
  background: linear-gradient(135deg, #409eff, #337ecc);
  box-shadow: 0 6px 18px rgba(64, 158, 255, 0.5);
  cursor: grab;
  user-select: none;
  touch-action: none;
}
.leo-operator-assistant-fab.is-open {
  box-shadow: 0 6px 18px rgba(64, 158, 255, 0.65), 0 0 0 3px rgba(64, 158, 255, 0.35);
}
.leo-operator-assistant-fab.is-dragging,
.leo-operator-assistant-fab:active {
  cursor: grabbing;
}
.leo-operator-assistant-fab:hover {
  filter: brightness(1.06);
}
</style>



// WEBPACK FOOTER //
// src/components/LeoKnowledge/LeoTrainingOperatorAssistantHost.vue